// external
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Paper,
  Box,
  IconButton,
  Fade,
  CircularProgress,
  Typography,
  Collapse,
  Button,
  DialogActions,
  DialogContentText,
  DialogContent,
  DialogTitle,
  Dialog,
  TextField,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { format, parseISO } from 'date-fns';

// internal
import './coe-weekly-planners.component.css';
import COEEllipsisPopover from '../../general/coe-ellipsis-popover/coe-ellipsis-popover.component';
import { Group, Team } from '../../../models/training.models';
import COEWeeklyPlanBuilderComponent from '../coe-weekly-plan-builder/coe-weekly-plan-builder.component';
import TrainingLibraryService from '../../../services/training-library.service';
import useService from '../../../hooks/service.hook';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import { palette } from '../../../theme';

export interface COEWeeklyPlannerModalProps {
  team: Team;
  group?: Group;
  startDate: string;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

const COEWeeklyPlannersComponent: FC<COEWeeklyPlannerModalProps> = (props) => {
  const trainingLibraryService = useService(TrainingLibraryService);
  const { showSnackbar } = useSnackbarHelper();

  const [loading, setLoading] = useState<boolean>(false);
  const [error] = useState<string | null>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [deletePlanId, setDeletePlanId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deletingPlan, setDeletingPlan] = useState<boolean>(false);
  const [createPlanModalOpen, setCreatePlanModalOpen] = useState<boolean>(false);
  const [planBuilderState, setPlanBuilderState] = useState<'create' | 'edit' | null>(null);

  useEffect(() => {
    if (props.open) {
      getWeeklyPlans();
      setSearchQuery('');
    }
  }, [props.open]);

  const filteredPlans = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return plans;
    return plans.filter(
      (plan) =>
        (plan.title || '').toLowerCase().includes(query) || (plan.description || '').toLowerCase().includes(query)
    );
  }, [plans, searchQuery]);

  const handleLoadPlan = (planId: string) => {
    const payload = {
      start_date: props.startDate,
      group_id: props.group?.id ?? null,
    };
    setLoading(true);
    trainingLibraryService
      .loadWeeklyTrainingPlan(props.team.id, planId, payload)
      .then(() => {
        setLoading(false);
        showSnackbar('Successfully loaded weekly plan', 'success');
        props.onRefresh();
        props.onClose();
      })
      .catch(() => {
        setLoading(false);
        showSnackbar('There was an error while loading weekly plan. Please try again later', 'error');
      });
  };

  const handleCreatePlan = () => {
    setSelectedPlanId(null);
    setPlanBuilderState('create');
    setCreatePlanModalOpen(true);
  };

  const handleCloseCreatePlanModal = () => {
    setCreatePlanModalOpen(false);
    setSelectedPlanId(null);
    setPlanBuilderState(null);
    getWeeklyPlans();
  };

  const handleItemClick = (planId: string) => {
    setExpandedItemId(expandedItemId === planId ? null : planId);
  };

  const handleEdit = (planId: string) => {
    setSelectedPlanId(planId);
    setPlanBuilderState('edit');
    setCreatePlanModalOpen(true);
  };

  const handleDelete = (planId: string) => {
    setDeletePlanId(planId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!deletePlanId) return;

    const teamId = props.team?.id;
    if (!teamId) return;

    setDeletingPlan(true);

    trainingLibraryService
      .deleteWeeklyTrainingPlan(teamId, deletePlanId)
      .then(() => {
        setDeletingPlan(false);
        setDeleteDialogOpen(false);
        setDeletePlanId(null);
        showSnackbar('Successfully deleted weekly plan', 'success');
        getWeeklyPlans();
      })
      .catch(() => {
        setDeletingPlan(false);
        showSnackbar('There was an error while deleting this weekly plan. Please try again later', 'error');
      });
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeletePlanId(null);
  };

  const getWeeklyPlans = () => {
    setLoading(true);
    const teamId = props.team?.id;
    if (!teamId) return;
    trainingLibraryService
      .getWeeklyTrainingPlans(teamId)
      .then((response) => {
        setPlans(response.data.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        showSnackbar('There was an error while fetching weekly plans. Please try again later', 'error');
      });
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    try {
      return format(parseISO(value), 'MM/dd/yyyy');
    } catch {
      return value;
    }
  };

  return (
    <>
      <Modal
        open={props.open}
        onClose={props.onClose}
        closeAfterTransition
        disableEscapeKeyDown={false}
        className="coe-weekly-planner-modal-container"
      >
        <Fade in={props.open} timeout={500}>
          <Paper className="coe-weekly-planner-modal-paper">
            {loading ? (
              <Box className="coe-weekly-planner-modal-loading">
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                <Box className="coe-weekly-planner-modal-gradient-header">
                  <IconButton
                    onClick={props.onClose}
                    className="coe-weekly-planner-modal-gradient-close"
                    aria-label="Close"
                    size="small"
                  >
                    <CloseIcon style={{ fontSize: 18 }} />
                  </IconButton>
                  <Box className="coe-weekly-planner-modal-gradient-title-block">
                    <h3 className="coe-weekly-planner-modal-gradient-title">Weekly Training Plans</h3>
                    <p className="coe-weekly-planner-modal-gradient-subtitle">
                      Select a plan to load saved templates as training drafts for the current week
                    </p>
                  </Box>
                </Box>

                <Box className="coe-weekly-planner-modal-content">
                  <div className="weekly-planner-container">
                    {error ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
                        <Typography variant="body1" style={{ color: palette.error.dark }}>
                          {error}
                        </Typography>
                      </div>
                    ) : (
                      <>
                        <div className="weekly-planner-toolbar">
                          <TextField
                            className="weekly-planner-search"
                            placeholder="Search plans..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="small"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: 16, color: palette.text.disabled }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Button
                            className="weekly-planner-create-btn"
                            variant="contained"
                            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={handleCreatePlan}
                          >
                            Create New
                          </Button>
                        </div>

                        <Typography className="weekly-planner-section-label">Saved Templates</Typography>

                        {plans.length === 0 ? (
                          <div className="weekly-planner-empty">
                            <CalendarMonthOutlinedIcon sx={{ fontSize: 40, color: palette.divider, mb: 1.5 }} />
                            <Typography className="weekly-planner-empty-title">No plans yet</Typography>
                            <Typography className="weekly-planner-empty-subtitle">
                              Create your first weekly plan template below
                            </Typography>
                          </div>
                        ) : filteredPlans.length === 0 ? (
                          <Typography
                            variant="body2"
                            style={{ color: palette.text.secondary, textAlign: 'center', padding: 24 }}
                          >
                            No plans match your search
                          </Typography>
                        ) : (
                          <div className="weekly-planner-list">
                            {filteredPlans.map((trainingPlan) => (
                              <div
                                key={trainingPlan.id}
                                className="weekly-planner-list-item"
                                onClick={() => handleItemClick(trainingPlan.id)}
                              >
                                <div className="weekly-planner-list-item-main">
                                  <div className="weekly-planner-list-icon">
                                    <CalendarMonthOutlinedIcon sx={{ fontSize: 15, color: palette.primary.main }} />
                                  </div>
                                  <div className="weekly-planner-list-text">
                                    <Typography className="weekly-planner-list-title">{trainingPlan.title}</Typography>
                                    <Typography className="weekly-planner-list-desc">
                                      {trainingPlan.description}
                                    </Typography>
                                  </div>
                                  <div className="weekly-planner-list-meta">
                                    <div className="weekly-planner-list-updated">
                                      <Typography className="weekly-planner-list-updated-label">Updated</Typography>
                                      <Typography className="weekly-planner-list-updated-value">
                                        {formatDate(trainingPlan.updated_at)}
                                      </Typography>
                                    </div>
                                    <IconButton
                                      size="small"
                                      className="weekly-planner-load-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLoadPlan(trainingPlan.id);
                                      }}
                                      aria-label="Load plan"
                                    >
                                      <AddIcon style={{ color: palette.primary.main, fontSize: 18 }} />
                                    </IconButton>
                                    <div
                                      className="weekly-planner-ellipsis-wrapper"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <COEEllipsisPopover
                                        actions={[
                                          {
                                            title: 'Edit Plan',
                                            color: 'info',
                                            action: () => handleEdit(trainingPlan.id),
                                          },
                                          {
                                            title: 'Delete Plan',
                                            color: 'error',
                                            action: () => handleDelete(trainingPlan.id),
                                          },
                                        ]}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <Collapse in={expandedItemId === trainingPlan.id} timeout="auto" unmountOnExit>
                                  <div className="weekly-planner-item-expanded">
                                    {(
                                      [
                                        'monday',
                                        'tuesday',
                                        'wednesday',
                                        'thursday',
                                        'friday',
                                        'saturday',
                                        'sunday',
                                      ] as const
                                    ).map((day) => (
                                      <div key={day} className="weekly-planner-expanded-row">
                                        <span className="weekly-planner-expanded-label-text">
                                          {day.charAt(0).toUpperCase() + day.slice(1)}:
                                        </span>
                                        <span className="weekly-planner-expanded-value">
                                          {((trainingPlan.plan ?? {})[day] ?? []).length} sessions
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </Collapse>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Box>
              </>
            )}
          </Paper>
        </Fade>
      </Modal>

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-plan-dialog-title"
        aria-describedby="delete-plan-dialog-description"
      >
        <DialogTitle id="delete-plan-dialog-title">Delete Weekly Plan</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-plan-dialog-description">
            Are you sure you want to delete this weekly plan? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deletingPlan}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deletingPlan}>
            {deletingPlan ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <COEWeeklyPlanBuilderComponent
        open={createPlanModalOpen}
        onClose={handleCloseCreatePlanModal}
        team={props.team}
        planId={selectedPlanId}
        type={planBuilderState}
      />
    </>
  );
};

export default COEWeeklyPlannersComponent;
